import { useCallback } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { useHttpClient } from "@/core/http/HttpClientContext";
import { useAppStore } from "@/core/store/useAppStore";
import { tasksService } from "../services/tasksService";
import { retryUnlessNotFound } from "../taskQueryRules";
import {
  AgencyTaskFilters,
  CreateTaskRequest,
  EMPTY_TASK_LIST,
  ReorderTasksRequest,
  Task,
  TaskAssigneeOption,
  TaskLinkRef,
  TaskLinkType,
  UpdateTaskRequest,
  assignablePeopleKeys,
  taskKeys,
} from "../types";

/** The idea's checklist: its own tasks, then its via groups (DD-052). */
export function useIdeaTasksQuery(ideaId: string | undefined) {
  const http = useHttpClient();
  return useQuery({
    queryKey: ideaId ? taskKeys.entity("idea", ideaId) : ["tasks", "none"],
    queryFn: async () => {
      if (!ideaId) return EMPTY_TASK_LIST;
      return tasksService.getIdeaTasks(http, ideaId);
    },
    enabled: !!ideaId,
  });
}

export function useStandaloneTasksQuery(clientId: string | undefined) {
  const http = useHttpClient();
  return useQuery({
    queryKey: clientId ? taskKeys.standalone(clientId) : ["tasks", "none"],
    queryFn: async () => {
      if (!clientId) return [];
      return tasksService.getStandaloneTasks(http, clientId);
    },
    enabled: !!clientId,
  });
}

/** The brief's shot list, in the shared list shape (DD-022, DD-052). */
export function useShotListQuery(briefId: string | undefined) {
  const http = useHttpClient();
  return useQuery({
    queryKey: briefId ? taskKeys.shotList(briefId) : ["tasks", "none"],
    queryFn: async () => {
      if (!briefId) return null;
      return tasksService.getShotList(http, briefId);
    },
    enabled: !!briefId,
  });
}

/** The agency Tasks tab (TD-094). Always stale and refetched on focus: a shared team view whose tasks also change from other surfaces. The key sits under `taskKeys.all`, so
 * every task mutation and a person rename refetch it. A filter switch keeps
 * the previous list as placeholder data: `isLoading` stays false, so the
 * panel keeps its filter row instead of hiding it behind the spinner
 * (WorkspacePanel hides `subheader` while `loading`). */
export function useAgencyTasksQuery(filters: AgencyTaskFilters) {
  const http = useHttpClient();
  return useQuery({
    queryKey: taskKeys.agency(filters),
    queryFn: () => tasksService.getAgencyTasks(http, filters),
    placeholderData: keepPreviousData,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * Everyone a task on this client can be assigned to or tagged with: the
 * agency team plus the client's contacts (TD-086). In the portal it reads
 * `/api/client-portal/people`, which only a session that may edit answers,
 * so callers gate with ``enabled: !!clientId``.
 */
export function useClientPeopleQuery(clientId: string | undefined) {
  const http = useHttpClient();
  return useQuery({
    queryKey: assignablePeopleKeys.client(clientId || "none"),
    queryFn: async (): Promise<TaskAssigneeOption[]> => {
      if (!clientId) return [];
      return tasksService.getAssignablePeople(http, clientId);
    },
    enabled: !!clientId,
  });
}

/** Create a task on any client the caller can reach, through
 * `POST /api/clients/<id>/tasks`, with the list's starting links. The task
 * drawer's create mode uses it on every surface (DD-051). */
export function useCreateTaskForClientMutation() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ clientId, data }: { clientId: string; data: CreateTaskRequest }) =>
      tasksService.createClientTask(http, clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useUpdateTaskMutation(onError?: () => void) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, data }: { taskId: string; data: UpdateTaskRequest }) =>
      tasksService.updateTask(http, taskId, data),
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/** The check circle's one-tap toggle, on rows and in the drawer header.
 * A failure toasts. `onDone` gets the server's copy of the task. */
export function useToggleTask() {
  const addToast = useAppStore((state) => state.addToast);
  // On the mutation, not the call: per-call callbacks fire only for the
  // latest of several quick taps.
  const { mutate } = useUpdateTaskMutation(() =>
    addToast({ message: "Impossibile aggiornare il task.", type: "error" })
  );
  return useCallback(
    (task: Task, onDone?: (updated: Task) => void) =>
      mutate(
        { taskId: task.id, data: { status: task.status === "done" ? "open" : "done" } },
        { onSuccess: (updated) => onDone?.(updated) }
      ),
    [mutate]
  );
}

/** Refreshes every task query except the gone task's own detail and notes:
 * they stay mounted until the drawer closes, and a refetch would only 404. */
export function invalidateTasksAfterGone(queryClient: QueryClient, taskId: string) {
  return queryClient.invalidateQueries({
    queryKey: taskKeys.all,
    predicate: (query) => !query.queryKey.includes(taskId),
  });
}

export function useDeleteTaskMutation() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (taskId: string) => tasksService.deleteTask(http, taskId),
    onSuccess: (_data, taskId) => {
      void invalidateTasksAfterGone(queryClient, taskId);
    },
  });
}

/** Orders one item's own tasks. No list offers drag reorder yet. */
export function useReorderTasksMutation() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (request: ReorderTasksRequest) => tasksService.reorderTasks(http, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/** One task with its direct and carried links (TD-096). Fetched on every
 * open (`staleTime: 0`). A 404 means it was deleted elsewhere: never
 * retried, so the drawer closes at once. Admin only: the creator drawer
 * passes `enabled: false`. */
export function useTaskQuery(taskId: string | undefined, enabled: boolean) {
  const http = useHttpClient();
  return useQuery({
    queryKey: taskKeys.detail(taskId ?? "none"),
    queryFn: () => {
      if (!taskId) throw new Error("No task to load");
      return tasksService.getTask(http, taskId);
    },
    enabled: enabled && !!taskId,
    staleTime: 0,
    retry: retryUnlessNotFound,
  });
}

/** A task's notes, newest first (DD-050). Team only: only the admin drawer
 * mounts the notes section. */
export function useTaskNotesQuery(taskId: string) {
  const http = useHttpClient();
  return useQuery({
    queryKey: taskKeys.notes(taskId),
    queryFn: () => tasksService.getTaskNotes(http, taskId),
    staleTime: 0,
  });
}

/** Add a typed note. Never retried: a retry after a lost response would
 * write the note twice. */
export function useAddTaskNoteMutation(taskId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => tasksService.addTaskNote(http, taskId, body),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/** Link the task to an item. The lists' via groups change with it. */
export function useAddTaskLinkMutation(taskId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ref: TaskLinkRef) => tasksService.addTaskLink(http, taskId, ref),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/** Never retried: a retry after a lost response would 404 on a link that
 * is already gone. */
export function useRemoveTaskLinkMutation(taskId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => tasksService.removeTaskLink(http, taskId, linkId),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

/** The link picker's search. The previous rows stay, dimmed, while the
 * next search loads. */
export function useTaskLinkOptionsQuery(clientId: string, type: TaskLinkType, q: string) {
  const http = useHttpClient();
  return useQuery({
    queryKey: taskKeys.linkOptions(clientId, type, q),
    queryFn: () => tasksService.getTaskLinkOptions(http, clientId, type, q),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export type { Task };
